import { useMemo, useState } from 'react';
import { Search, Plus, Pencil, Trash2, Users } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAllTeams, type AdminTeam } from '@/hooks/useAllTeams';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TeamFormModal } from '@/components/modals/TeamFormModal';
import { TeamMembersModal } from '@/components/modals/TeamMembersModal';
import { TeamMembershipImportModal } from '@/components/modals/TeamMembershipImportModal';

export const TeamsManagementPanel = () => {
  const { data: teams = [], isLoading } = useAllTeams();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<AdminTeam | null>(null);
  const [membersTeamId, setMembersTeamId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminTeam | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return teams;
    const q = search.toLowerCase();
    return teams.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q)
    );
  }, [teams, search]);

  const deleteTeam = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('teams').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-teams'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('Time excluído');
      setDeleteTarget(null);
    },
    onError: (err: Error) => {
      toast.error(`Erro ao excluir time: ${err.message}`);
    },
  });

  const openCreate = () => {
    setEditingTeam(null);
    setFormOpen(true);
  };

  const openEdit = (team: AdminTeam) => {
    setEditingTeam(team);
    setFormOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats + Add */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-secondary">
          {teams.length} {teams.length === 1 ? 'time' : 'times'}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={openCreate} size="sm" className="gap-1.5">
            <Plus className="w-4 h-4" />
            Novo Time
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
        <input
          type="text"
          placeholder="Buscar por nome ou descrição..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full h-9 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
        />
      </div>

      {/* Table */}
      <div className="rounded-lg border border-color overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary">
              <TableHead className="w-12"></TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center">Membros</TableHead>
              <TableHead>Criado em</TableHead>
              <TableHead className="w-32 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((team) => (
              <TableRow key={team.id}>
                <TableCell className="text-xl">{team.icon}</TableCell>
                <TableCell className="font-medium text-primary">{team.name}</TableCell>
                <TableCell className="text-secondary text-sm max-w-md truncate">
                  {team.description || '—'}
                </TableCell>
                <TableCell className="text-center text-secondary text-sm">
                  {team.member_count}
                </TableCell>
                <TableCell className="text-secondary text-sm">
                  {new Date(team.created_at).toLocaleDateString('pt-BR')}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setMembersTeamId(team.id)}
                      title="Gerenciar membros"
                    >
                      <Users className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => openEdit(team)}
                      title="Editar"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-tertiary hover:text-destructive"
                      onClick={() => setDeleteTarget(team)}
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-secondary py-8">
                  Nenhum time encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modals */}
      <TeamFormModal
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) {
            queryClient.invalidateQueries({ queryKey: ['admin-teams'] });
          }
        }}
        team={editingTeam as any}
      />

      <TeamMembershipImportModal
        open={importOpen}
        onOpenChange={setImportOpen}
        teamNames={teams.map((team) => team.name)}
      />

      {membersTeamId && (
        <TeamMembersModal
          open={!!membersTeamId}
          onOpenChange={(open) => !open && setMembersTeamId(null)}
          teamId={membersTeamId}
          isAdmin={true}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir time</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o time <strong>{deleteTarget?.name}</strong>?
              Esta ação removerá todos os membros e não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteTeam.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
